import { useEffect, useState } from 'react';
import { reportConversion } from '../utils/gtag';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

// Simple countdown hook toward an arbitrary promo end date (adjust as needed)
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetDate - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(Math.max(0, targetDate - Date.now())), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  const seconds = Math.floor(timeLeft / 1000);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return { d, h, m, s, finished: timeLeft <= 0 };
}

export default function Offers() {
  const { t } = useTranslation();
  
  // Define offers inside component to access t()
  const offers = [
    {
      id: 1,
      tag: t('offers.offer1.tag'),
      title: t('offers.offer1.title'),
      desc: t('offers.offer1.desc'),
      code: 'SCANFREE',
      expiresInDays: 60,
      category: t('offers.offer1.category')
    },
    {
      id: 2,
      tag: t('offers.offer2.tag'),
      title: t('offers.offer2.title'),
      desc: t('offers.offer2.desc'),
      code: 'OILPLUS',
      expiresInDays: 20,
      category: t('offers.offer2.category')
    },
    {
      id: 3,
      tag: t('offers.offer3.tag'),
      title: t('offers.offer3.title'),
      desc: t('offers.offer3.desc'),
      code: 'COOLAIR',
      expiresInDays: 14,
      category: t('offers.offer3.category')
    },
    {
      id: 4,
      tag: t('offers.offer4.tag'),
      title: t('offers.offer4.title'),
      desc: t('offers.offer4.desc'),
      code: 'STOPSAFE10',
      expiresInDays: 5,
      category: t('offers.offer4.category')
    },
    {
      id: 5,
      tag: t('offers.offer5.tag'),
      title: t('offers.offer5.title'),
      desc: t('offers.offer5.desc'),
      code: 'TUNEBASE',
      expiresInDays: 11,
      category: t('offers.offer5.category')
    }
  ];
  
  // Set a base reference date: now + longest offer days to unify visual countdown (or per-offer target)
  const now = Date.now();
  const augmented = offers.map(o => ({ ...o, target: now + o.expiresInDays * 24 * 3600 * 1000 }));
  return (
    <>
      <SEO
        title="Special Offers - Car Service Ras Al Khor Dubai | Free Diagnostics & Deals"
        description="Exclusive car service offers at Vigor Auto Ras Al Khor Dubai - Free computer diagnostics, oil change packages, AC service deals. Affordable auto repair for European, Japanese, Korean cars. Serving Nad Al Hamar, Al Awir & International City."
        keywords="car service offers dubai, car repair deals Ras Al Khor, free car diagnostics dubai, oil change offers dubai, car AC service deals, affordable car service dubai, car maintenance packages Ras Al Khor, auto repair promotions dubai, car garage offers Ras Al Khor"
      />
    <div className="bg-slate-50">
      {/* Hero */}
  <section className="pt-20 md:pt-28 pb-12 md:pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,#F5D313,transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <p className="uppercase tracking-widest text-xs font-semibold text-[#F5D313]/80">{t('offers.badge')}</p>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{t('offers.title')}</h1>
            <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed">{t('offers.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Offers Grid */}
  <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{t('offers.featured')}</h2>
              <p className="text-slate-600 leading-relaxed">{t('offers.featuredDesc')}</p>
            </div>
            <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#F5D313]"></span> {t('offers.active')}</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300"></span> {t('offers.pending')}</span>
            </div>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {augmented.map(o => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
  <section className="py-10 md:py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">{t('offers.cta.title')}</h2>
          <p className="mt-4 max-w-2xl mx-auto text-slate-300 text-sm md:text-base">{t('offers.cta.subtitle')}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              onClick={(e) => { reportConversion(window.location.origin + '/contact'); }}
              className="inline-flex justify-center rounded-lg bg-[#F5D313] px-6 py-3 font-medium text-slate-900 hover:bg-[#e4c40f] transition-colors shadow"
            >{t('common.bookNow')}</a>
            <a href="tel:+971568699800" onClick={() => reportConversion('tel:+971568699800')} className="inline-flex justify-center rounded-lg border border-slate-300 px-6 py-3 font-medium text-white hover:bg-white/10 transition-colors shadow-sm">{t('common.callUs')}</a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}

function OfferCard({ offer }) {
  const { t } = useTranslation();
  const { d, h, m, s, finished } = useCountdown(offer.target);
  return (
    <div className="relative p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#F5D313] via-[#F5D313]/60 to-transparent" />
      <div className="flex items-start justify-between mb-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#F5D313] bg-[#F5D313]/10 px-2 py-1 rounded">{offer.tag}</span>
        <span className="text-[10px] font-medium text-slate-400">{offer.category}</span>
      </div>
      <h3 className="font-semibold text-slate-800 text-lg mb-2 leading-snug">{offer.title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed flex-1">{offer.desc}</p>
      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <code className="text-xs font-semibold tracking-wide bg-slate-900 text-[#F5D313] px-2.5 py-1 rounded">{offer.code}</code>
        <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500">
          {!finished ? (
            <>
              <TimeBlock label="D" value={d} />
              <TimeBlock label="H" value={h} />
              <TimeBlock label="M" value={m} />
              <TimeBlock label="S" value={s} />
            </>
          ) : (
            <span className="text-red-500">{t('offers.expired')}</span>
          )}
        </div>
      </div>
      <a
  href={`https://wa.me/971524333690?text=${encodeURIComponent(`Hi Vigor Auto, I'd like to redeem offer ${offer.code} (${offer.title}).`)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Redeem ${offer.title} via WhatsApp`}
  onClick={() => reportConversion('https://wa.me/971524333690')}
        className="mt-6 w-full inline-flex items-center justify-center rounded-lg bg-slate-900 text-white text-sm font-semibold py-2.5 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 transition"
      >
        {t('offers.redeem')}
      </a>
    </div>
  );
}

function TimeBlock({ label, value }) {
  return (
    <div className="flex flex-col items-center bg-slate-100 rounded px-1.5 py-1 min-w-[2.4rem]">
      <span className="text-xs font-bold text-slate-800 tabular-nums leading-none">{String(value).padStart(2,'0')}</span>
      <span className="text-[9px] font-medium tracking-wide text-slate-500 mt-0.5">{label}</span>
    </div>
  );
}
